import React, { useState, useRef } from 'react';
import {
    Trash2, Copy,
    Wallet, Activity, TrendingUp, TrendingDown, 
    AlertTriangle, ArrowRightLeft, History, Download, 
    UploadCloud, Server, Zap, RefreshCw, ShieldAlert,
    CheckCircle2, FileText, DollarSign, Banknote, Building2, Check, X, Clock, ExternalLink, Filter, Search
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { authFetch } from '../utils/apiClient';

// --- MOCK DATA FOR OTHER TABS ---
const MOCK_HERO_STATS = {
    aum: 12450000.50,
    netFunding: 145000.00,
    variance: -12.50,
    gasBurn: 45.20,
    monthlyVolume: 42500
};

const MOCK_FRAGMENTED_WALLETS = [
    { id: 'w1', client: 'alex.t@email.com', network: 'ERC20', balance: 150.00, threshold: 200.00, estGas: 3.50, status: 'Waiting' },
    { id: 'w2', client: 'sarah.j@email.com', network: 'TRC20', balance: 85.00, threshold: 50.00, estGas: 0.45, status: 'Ready for Sweep' },
    { id: 'w3', client: 'm.chen@email.com', network: 'TRC20', balance: 500.00, threshold: 50.00, estGas: 0.45, status: 'Sweeping...' },
];

const MOCK_RECONCILIATION_LOG = [
    { id: 'r1', date: '2026-04-21 09:15', client: 'david.w@email.com', method: 'KoraPay', received: 1000.00, credited: 1000.00, variance: 0, status: 'Matched' },
    { id: 'r2', date: '2026-04-21 08:42', client: 'emma.l@email.com', method: 'Crypto (TRC20)', received: 500.00, credited: 500.00, variance: 0, status: 'Matched' },
    { id: 'r3', date: '2026-04-21 07:12', client: 'j.smith@email.com', method: 'Crypto (ERC20)', received: 248.50, credited: 250.00, variance: -1.50, status: 'Variance Alert' },
];

const MOCK_DAILY_REVENUE = [
    { date: '2026-06-11', volume: 1450, rate: 6.50, gross: 9425.00, partnerPayouts: 3125.00, net: 6300.00 },
    { date: '2026-06-10', volume: 1820, rate: 6.50, gross: 11830.00, partnerPayouts: 4100.00, net: 7730.00 },
    { date: '2026-06-09', volume: 1340, rate: 6.50, gross: 8710.00, partnerPayouts: 2950.00, net: 5760.00 },
    { date: '2026-06-08', volume: 1650, rate: 6.50, gross: 10725.00, partnerPayouts: 3800.00, net: 6925.00 },
    { date: '2026-06-07', volume: 950,  rate: 6.50, gross: 6175.00,  partnerPayouts: 1800.00, net: 4375.00 },
];

// --- REUSABLE COMPONENTS ---
const StatCard = ({ title, value, subtext, trend, icon: Icon, trendUp, isWarning }) => (
    <div className={`bg-slate-900 rounded-xl p-6 shadow-sm border ${isWarning ? 'border-red-500/50 shadow-red-900/10' : 'border-slate-800'} flex items-start justify-between`}>
        <div>
            <p className="text-sm font-medium text-slate-400 mb-1">{title}</p>
            <h3 className={`text-2xl font-bold ${isWarning ? 'text-red-400' : 'text-white'}`}>{value}</h3>
            <div className="flex items-center mt-2">
                {trend && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex items-center ${trendUp ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                        {trendUp ? <TrendingUp size={12} className="mr-1"/> : <TrendingDown size={12} className="mr-1"/>}
                        {trend}
                    </span>
                )}
                <span className="text-xs text-slate-500 ml-2">{subtext}</span>
            </div>
        </div>
        <div className={`p-3 rounded-lg border shadow-inner ${isWarning ? 'bg-red-950 border-red-900 text-red-500' : 'bg-slate-800 border-slate-700 text-amber-500'}`}>
            <Icon size={24} />
        </div>
    </div>
);

const FinancePage = () => {
    const [activeTab, setActiveTab] = useState('treasury');
    
    // --- NEW: LIVE VOLUME & REVENUE STATE ---
    const [liveMonthlyVolume, setLiveMonthlyVolume] = useState(0);
    const [dailyRevenueData, setDailyRevenueData] = useState([]); 

    // --- WITHDRAWALS STATE ---
    const [withdrawalsList, setWithdrawalsList] = useState([]);
    const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
    const [withdrawalCategory, setWithdrawalCategory] = useState('fiat'); // 'fiat' | 'crypto'
    const [withdrawalsFilter, setWithdrawalsFilter] = useState('all');
    const [withdrawalsSearch, setWithdrawalsSearch] = useState('');
    const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
    const [actionModalOpen, setActionModalOpen] = useState(false);
    const [actionType, setActionType] = useState('approve'); // 'approve' | 'decline'
    const [declineReason, setDeclineReason] = useState('');
    const [actionProcessing, setActionProcessing] = useState(false);
    const [actionFeedback, setActionFeedback] = useState(null);

    const [wireReference, setWireReference] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const [showLegacyWithdrawals, setShowLegacyWithdrawals] = useState(false);

    const handleCopyAddress = (text, id) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const parseCryptoDestination = (dest = '', gateway = '') => {
        if (!dest && !gateway) return { network: '', address: '', isInternal: false, walletType: '' };
        
        let clean = String(dest || '').trim();
        let isInternal = clean.toLowerCase().includes('nommia wallet') || clean.toLowerCase().includes('internal');
        let walletType = isInternal ? 'Nommia Wallet' : (clean.toLowerCase().includes('external') ? 'External' : '');
        let network = '';
        let address = clean;

        const parenMatch = clean.match(/\((.*?)\):\s*([a-zA-Z0-9]+)/);
        if (parenMatch) {
            network = parenMatch[1].trim();
            address = parenMatch[2].trim();
        } else if (clean.includes(':')) {
            const parts = clean.split(':');
            const prefix = parts[0].trim();
            address = parts.slice(1).join(':').trim();
            const netMatch = prefix.match(/\((.*?)\)/);
            if (netMatch) {
                network = netMatch[1].trim();
            } else {
                network = prefix;
            }
        }

        if (!network && gateway) {
            const gwMatch = gateway.match(/\((.*?)\)/);
            if (gwMatch) {
                const inside = gwMatch[1];
                if (inside.includes('-')) {
                    network = inside.split('-')[1].trim();
                } else {
                    network = inside;
                }
            }
        }

        if (!network) {
            if (address.startsWith('0x') && address.length === 42) {
                network = 'EVM / BSC';
            } else if (address.startsWith('T') && address.length === 34) {
                network = 'TRC20';
            }
        }

        return { network, address, isInternal, walletType };
    };

    // Categorization Helpers
    const isFiatItem = (w) => {
        if (!w) return false;
        const gw = (w.gateway || '').toLowerCase();
        const dest = (w.destination || '').toLowerCase();
        return gw.includes('bank') || gw.includes('wire') || dest.includes('bank') || Boolean(w.metadata?.bank_code);
    };

    const isKorapayItem = (w) => {
        if (!w) return false;
        const meta = w.metadata || {};
        const dest = (w.destination || '').toLowerCase();
        const gw = (w.gateway || '').toLowerCase();
        return meta.currency === 'NGN' || 
               meta.country === 'NG' || 
               Boolean(meta.bank_code) || 
               Boolean(w.amount_ngn) ||
               dest.includes('ngn') || 
               dest.includes('₦') ||
               (gw.includes('bank') && (dest.includes('gtbank') || dest.includes('bank') || dest.includes('|')));
    };

    const getKorapayMeta = (w) => {
        if (!w) return {};
        const meta = w.metadata || {};
        const dest = w.destination || '';
        const parts = dest.split('|').map(s => s.trim());
        let bank_name = meta.bank_name || '';
        let account_number = meta.account_number || '';
        let account_name = meta.account_name || '';
        let payout_amount_ngn = meta.payout_amount_ngn || w.amount_ngn || '';

        if (parts.length >= 1 && !bank_name) {
            bank_name = parts[0];
        }
        if (parts.length >= 2 && (!account_number || !account_name)) {
            const accMatch = parts[1].match(/^(\d{10})\s*(?:\(([^)]+)\))?/);
            if (accMatch) {
                if (!account_number) account_number = accMatch[1];
                if (!account_name) account_name = accMatch[2] || '';
            } else if (!account_number) {
                account_number = parts[1];
            }
        }
        if (parts.length >= 3 && !payout_amount_ngn) {
            const ngnMatch = parts[2].match(/(?:NGN:\s*₦?|₦)([\d,]+(?:\.\d+)?)/i);
            if (ngnMatch) {
                payout_amount_ngn = parseFloat(ngnMatch[1].replace(/,/g, ''));
            }
        }

        return {
            ...meta,
            bank_name,
            account_number,
            account_name,
            payout_amount_ngn,
            bank_code: meta.bank_code || '',
            currency: 'NGN',
            country: 'NG'
        };
    };

    const isManualBankItem = (w) => {
        if (!w) return false;
        return isFiatItem(w) && !isKorapayItem(w);
    };

    const isCryptoItem = (w) => !isFiatItem(w);

    const fetchWithdrawals = async (includeLegacy = showLegacyWithdrawals) => {
        setWithdrawalsLoading(true);
        try {
            let query = supabase
                .from('withdrawal_intents')
                .select('*')
                .order('created_at', { ascending: false });

            if (!includeLegacy) {
                query = query.gte('created_at', '2026-10-23T00:00:00Z');
            }

            const { data, error } = await query;

            if (error) throw error;
            setWithdrawalsList(data || []);
        } catch (err) {
            console.error("Failed to fetch withdrawal intents:", err);
        } finally {
            setWithdrawalsLoading(false);
        }
    };

    React.useEffect(() => {
        fetchWithdrawals();
    }, []);

        const BACKEND_URL = process.env.REACT_APP_NOMMIA_API_URL || 'https://nommia-crm-backend-production.up.railway.app';

    const handleExecuteApproval = async () => {
        if (!selectedWithdrawal) return;
        setActionProcessing(true);
        setActionFeedback(null);
        try {
            const isKora = isKorapayItem(selectedWithdrawal);
            const isWire = isManualBankItem(selectedWithdrawal);

            let endpoint = `${BACKEND_URL}/api/crm/withdrawals/approve-crypto`;
            let payload = { intentId: selectedWithdrawal.id };

            if (isKora) {
                endpoint = `${BACKEND_URL}/api/crm/withdrawals/approve-korapay`;
                payload.metadata = getKorapayMeta(selectedWithdrawal);
            } else if (isWire) {
                endpoint = `${BACKEND_URL}/api/crm/withdrawals/approve-manual-bank`;
                payload.wireReference = wireReference;
            }

            const res = await authFetch(endpoint, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data?.error || "Approval failed.");
            }

            setActionFeedback({ type: 'success', message: data.message || "Withdrawal approved and marked Completed in XValley!" });
            fetchWithdrawals();
            setTimeout(() => {
                setActionModalOpen(false);
                setSelectedWithdrawal(null);
                setWireReference('');
                setActionFeedback(null);
            }, 1800);
        } catch (err) {
            setActionFeedback({ type: 'error', message: err.message });
        } finally {
            setActionProcessing(false);
        }
    };

    const handleExecuteDecline = async () => {
        if (!selectedWithdrawal) return;
        setActionProcessing(true);
        setActionFeedback(null);
        try {
            const res = await authFetch(`${BACKEND_URL}/api/crm/withdrawals/decline`, {
                method: 'POST',
                body: JSON.stringify({ intentId: selectedWithdrawal.id, reason: declineReason })
            });

            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data?.error || "Failed to decline withdrawal.");
            }

            setActionFeedback({ type: 'success', message: "Withdrawal marked as declined and balance restored in XValley!" });
            fetchWithdrawals();
            setTimeout(() => {
                setActionModalOpen(false);
                setSelectedWithdrawal(null);
                setDeclineReason('');
                setActionFeedback(null);
            }, 1500);
        } catch (err) {
            setActionFeedback({ type: 'error', message: err.message });
        } finally {
            setActionProcessing(false);
        }
    };

    const handleExecuteDelete = async () => {
        if (!selectedWithdrawal) return;
        setActionProcessing(true);
        setActionFeedback(null);
        try {
            const res = await authFetch(`${BACKEND_URL}/api/crm/withdrawals/delete`, {
                method: 'POST',
                body: JSON.stringify({ intentId: selectedWithdrawal.id })
            });

            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data?.error || "Failed to delete withdrawal.");
            }

            setActionFeedback({ type: 'success', message: "Withdrawal marked Deleted in XValley and CRM!" });
            fetchWithdrawals();
            setTimeout(() => {
                setActionModalOpen(false);
                setSelectedWithdrawal(null);
                setActionFeedback(null);
            }, 1500);
        } catch (err) {
            setActionFeedback({ type: 'error', message: err.message });
        } finally {
            setActionProcessing(false);
        }
    }; 

    React.useEffect(() => {
        const fetchLiveFinanceData = async () => {
            try {
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

                // 1. Fetch live lot volume for the last 30 days
                const { data: volData, error: volError } = await supabase
                    .from('daily_volume_ledger')
                    .select('volume, created_at')
                    .gte('created_at', thirtyDaysAgo.toISOString());

                if (volError) throw volError;
                
                // 2. Fetch live IB Payouts for the last 30 days
                const { data: payoutData, error: payoutError } = await supabase
                    .from('wallet_ledger')
                    .select('amount, created_at')
                    .gte('created_at', thirtyDaysAgo.toISOString());

                if (payoutError) throw payoutError;

                // --- PROCESS DATA DAY-BY-DAY ---
                let totalLots = 0;
                const dailyMap = {};

                // Process Volume
                if (volData) {
                    volData.forEach(row => {
                        const lots = Number(row.volume || 0);
                        totalLots += lots;
                        
                        const dateStr = new Date(row.created_at).toLocaleDateString();
                        if (!dailyMap[dateStr]) dailyMap[dateStr] = { volume: 0, payouts: 0 };
                        dailyMap[dateStr].volume += lots;
                    });
                }

                // Process Payouts
                if (payoutData) {
                    payoutData.forEach(row => {
                        const dateStr = new Date(row.created_at).toLocaleDateString();
                        if (!dailyMap[dateStr]) dailyMap[dateStr] = { volume: 0, payouts: 0 };
                        // Ensure we only count positive commission payouts
                        if (Number(row.amount) > 0) {
                            dailyMap[dateStr].payouts += Number(row.amount);
                        }
                    });
                }

                setLiveMonthlyVolume(totalLots);

                // Build the final array for the Daily Commission Earnings table
                const BROKER_PROFIT_PER_LOT = 6.50;
                const processedDailyTable = Object.keys(dailyMap).map(date => {
                    const day = dailyMap[date];
                    const gross = day.volume * BROKER_PROFIT_PER_LOT;
                    return {
                        date: date,
                        volume: day.volume,
                        rate: BROKER_PROFIT_PER_LOT,
                        gross: gross,
                        partnerPayouts: day.payouts,
                        net: gross - day.payouts
                    };
                });

                // Sort by newest date first
                processedDailyTable.sort((a, b) => new Date(b.date) - new Date(a.date));
                setDailyRevenueData(processedDailyTable);

            } catch (err) {
                console.error("Error fetching live finance data:", err);
            }
        };
        fetchLiveFinanceData();
    }, []);

    // --- OTP STATE ---
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [isRequestingOtp, setIsRequestingOtp] = useState(false);
    const [isVerifyingSweep, setIsVerifyingSweep] = useState(false);

    // --- CSV ENGINE STATE ---
    const clientFileInputRef = useRef(null);
    const lpFileInputRef = useRef(null);
    const [clientDataRaw, setClientDataRaw] = useState(null);
    const [lpDataRaw, setLpDataRaw] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [slippageLog, setSlippageLog] = useState([]);

    // --- RESTORED: EXECUTION STATS STATE ---
    const [executionStats, setExecutionStats] = useState({
        totalTrades: 0,
        netPnL: 0,
        positiveTrades: 0,
        negativeTrades: 0
    });

    // --- NEW: LIVE FUNDING RECONCILIATION STATE ---
    const [reconciliationLog, setReconciliationLog] = useState([]);
    const [isLoadingFunding, setIsLoadingFunding] = useState(false);
    const [fundingStats, setFundingStats] = useState({
        fiatVariance: 0,
        cryptoVariance: 0,
        unmatched: 0
    });

    // --- OTP HANDLERS ---
    const handleRequestEmergencySweep = async () => {
        setIsRequestingOtp(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1000)); 
            alert("Security Code sent to your admin email.");
            setShowOtpModal(true);
        } catch (err) {
            alert(err.message);
        } finally {
            setIsRequestingOtp(false);
        }
    };

    const handleConfirmSweep = async () => {
        if (otpCode.length !== 6) {
            alert("Please enter the 6-digit security code.");
            return;
        }
        setIsVerifyingSweep(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            alert("Success! Emergency sweep initiated. Funds are moving to cold storage.");
            setShowOtpModal(false);
            setOtpCode('');
        } catch (err) {
            alert(err.message);
        } finally {
            setIsVerifyingSweep(false);
        }
    };

    // --- LIVE FUNDING FETCH & REAL-TIME ENGINE ---
    React.useEffect(() => {
        if (activeTab !== 'funding') return;

        const fetchLiveFundingData = async () => {
            setIsLoadingFunding(true);
            try {
                const { data, error } = await supabase 
                    .from('deposit_reconciliation')
                    .select('*')
                    .order('created_at', { ascending: false })
                    .limit(100);

                if (error) throw error;

                if (data) {
                    setReconciliationLog(data);
                    
                    let fiatVar = 0, cryptoVar = 0, unmatchedCount = 0;
                    data.forEach(row => {
                        if (row.status === 'Variance Alert' || Number(row.variance) !== 0) {
                            if (row.method.toLowerCase().includes('korapay')) fiatVar += Number(row.variance);
                            else cryptoVar += Number(row.variance);
                        }
                        if (row.status === 'Unmatched' || row.status === 'Failed') unmatchedCount++;
                    });

                    setFundingStats({ fiatVariance: fiatVar, cryptoVariance: cryptoVar, unmatched: unmatchedCount });
                }
            } catch (err) {
                console.error("Error fetching funding data:", err);
            } finally {
                setIsLoadingFunding(false);
            }
        };

        // 1. Fetch the historical data immediately
        fetchLiveFundingData();
        
        // 2. Subscribe to LIVE incoming deposits from KoraPay!
        const channel = supabase
            .channel('live-reconciliation')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'deposit_reconciliation' }, (payload) => {
                console.log('🚨 New Deposit Detected!', payload.new);
                
                // Instantly pop the new deposit at the top of the table
                setReconciliationLog(currentLogs => [payload.new, ...currentLogs]);
                
                // Update the stats header if there is a variance
                if (payload.new.status === 'Variance Alert' || Number(payload.new.variance) !== 0) {
                    setFundingStats(prev => ({
                        ...prev,
                        fiatVariance: payload.new.method.toLowerCase().includes('korapay') 
                            ? prev.fiatVariance + Number(payload.new.variance) 
                            : prev.fiatVariance,
                        cryptoVariance: !payload.new.method.toLowerCase().includes('korapay') 
                            ? prev.cryptoVariance + Number(payload.new.variance) 
                            : prev.cryptoVariance
                    }));
                }
            })
            .subscribe();

        // Cleanup the listener when you click away from the tab
        return () => {
            supabase.removeChannel(channel);
        };
    }, [activeTab]);

    // --- DUAL CSV PARSING ENGINE ---
    const readFileAsText = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(e);
            reader.readAsText(file);
        });
    };

    // Bulletproof CSV Parser
    const parseCSV = (rawText) => {
        // 1. Strip invisible BOM character that breaks headers
        const text = rawText.replace(/^\uFEFF/, '').trim();
        const rows = text.split(/\r?\n/).filter(row => row.trim().length > 0);
        if (rows.length < 2) return [];

        // 2. Character-by-character line parser to perfectly handle empty columns (,,) and quotes
        const parseLine = (line) => {
            const result = [];
            let current = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    result.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }
            result.push(current.trim());
            return result;
        };

        const headers = parseLine(rows[0]).map(h => h.toLowerCase());
        
        return rows.slice(1).map(row => {
            const values = parseLine(row);
            const obj = {};
            headers.forEach((header, index) => {
                obj[header] = values[index] || '';
            });
            return obj;
        });
    };

    const handleClientFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) setClientDataRaw(parseCSV(await readFileAsText(file)));
    };

    const handleLpFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) setLpDataRaw(parseCSV(await readFileAsText(file)));
    };

    const runVarianceAnalysis = () => {
        if (!clientDataRaw || !lpDataRaw) return;
        setIsAnalyzing(true);

        setTimeout(() => {
            const joinedData = [];
            let currentNetPnL = 0, posCount = 0, negCount = 0;

            // 1. Build LP Map (Fuzzy matching for various header names)
            const lpTradeMap = {};
            lpDataRaw.forEach(row => {
                const lpTradeId = row.trade || row['trade id'] || row['order id'] || row.ticket || row.id;
                if (lpTradeId) lpTradeMap[String(lpTradeId).trim()] = row;
            });

            // 2. Loop Client Trades (Fuzzy matching for various header names)
            clientDataRaw.forEach(clientRow => {
                const clientTicketId = clientRow.ticket || clientRow.position || clientRow.order || clientRow.deal || clientRow.id;
                if (!clientTicketId) return;
                
                const rawTicket = String(clientTicketId).trim();

                // Bulletproof Matcher: Check if LP Trade ID is a suffix of the Client Ticket
                let lpMatch = lpTradeMap[rawTicket];
                if (!lpMatch) {
                    const matchedKey = Object.keys(lpTradeMap).find(lpKey => rawTicket.endsWith(lpKey));
                    if (matchedKey) lpMatch = lpTradeMap[matchedKey];
                }

                if (lpMatch) {
                    const asset = clientRow.instrument || clientRow.symbol || clientRow.item || 'UNKNOWN';
                    const direction = String(clientRow.side || clientRow.type || clientRow.cmd || 'BUY').toUpperCase();
                    const rawVolume = parseFloat(clientRow.volume || clientRow.lots || clientRow.qty || 0);
                    
                    const clientPrice = parseFloat(clientRow['opening price'] || clientRow['open price'] || clientRow.price || 0);
                    const lpPrice = parseFloat(lpMatch['average price'] || lpMatch['avg price'] || lpMatch.price || lpMatch['fill price'] || 0);

                    // Pip Multiplier Logic
                    const isJPY = asset.includes('JPY');
                    const isGold = asset.includes('XAU') || asset.includes('XAG') || asset.includes('XPD');
                    const pipMultiplier = isJPY ? 100 : (isGold ? 10 : 10000);

                    let variancePips = 0;
                    let pnlImpact = 0;

                    if (direction.includes('BUY')) {
                        variancePips = (clientPrice - lpPrice) * pipMultiplier;
                        pnlImpact = (clientPrice - lpPrice) * rawVolume;
                    } else {
                        variancePips = (lpPrice - clientPrice) * pipMultiplier;
                        pnlImpact = (lpPrice - clientPrice) * rawVolume;
                    }

                    currentNetPnL += pnlImpact;
                    if (pnlImpact > 0) posCount++;
                    if (pnlImpact < 0) negCount++;

                    joinedData.push({
                        id: rawTicket,
                        lpId: lpMatch.trade || lpMatch['trade id'] || lpMatch['order id'] || lpMatch.ticket || 'Unknown',
                        client: clientRow.username || clientRow.login || clientRow.account || 'Unknown',
                        asset: asset,
                        direction: direction,
                        volume: rawVolume,
                        clientPrice: clientPrice,
                        lpPrice: lpPrice,
                        variancePips: Math.round(variancePips * 10) / 10,
                        pnlImpact: pnlImpact
                    });
                }
            });

            setSlippageLog(joinedData);
            setExecutionStats({
                totalTrades: joinedData.length,
                netPnL: currentNetPnL,
                positiveTrades: posCount,
                negativeTrades: negCount
            });
            setIsAnalyzing(false);

            // Give explicit feedback so the app doesn't feel broken
            if (joinedData.length === 0) {
                alert(`Analysis complete: 0 matches found.\n\nScanned ${clientDataRaw.length} client rows against ${lpDataRaw.length} LP rows.\nPlease verify your CSV files contain matching Ticket/Order IDs.`);
            } else {
                alert(`Analysis complete: Successfully matched ${joinedData.length} trades!`);
            }

        }, 800);
    };


    return (
        <div className="p-4 md:p-8 animate-fade-in relative">

            {/* --- OTP SECURITY MODAL --- */}
            {showOtpModal && (
                <div className="absolute inset-0 bg-slate-950/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-red-500/50 rounded-2xl w-full max-w-sm shadow-2xl shadow-red-900/20 p-8 animate-fade-in text-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 to-amber-500"></div>
                        <ShieldAlert size={48} className="text-red-500 mx-auto mb-4 animate-pulse" />
                        <h3 className="text-xl font-bold text-white mb-2">Security Verification</h3>
                        <p className="text-xs text-slate-400 mb-6">
                            An authorization code has been sent to your registered admin email. Please enter it below to confirm the emergency sweep.
                        </p>
                        <input 
                            type="text" 
                            maxLength="6"
                            placeholder="000000"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-4 text-center text-3xl font-mono text-white tracking-[0.5em] focus:ring-2 focus:ring-red-500 outline-none mb-6"
                        />
                        <div className="flex gap-3">
                            <button 
                                onClick={() => { setShowOtpModal(false); setOtpCode(''); }}
                                disabled={isVerifyingSweep}
                                className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleConfirmSweep}
                                disabled={isVerifyingSweep || otpCode.length !== 6}
                                className="flex-1 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-500 transition-colors disabled:opacity-50"
                            >
                                {isVerifyingSweep ? 'Verifying...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* GLOBAL HEADER */}
            <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Finance Operations</h2>
                <p className="text-slate-400 mt-1">Automated reconciliation, treasury sweeping, and execution variance.</p>
            </div>

            {/* HERO STATS ROW */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
                <StatCard 
                    title="Platform AUM" 
                    value={`$${MOCK_HERO_STATS.aum.toLocaleString(undefined, {minimumFractionDigits: 2})}`} 
                    subtext="Total client equity" 
                    trend="Live" trendUp={true} icon={Wallet} 
                />
                <StatCard 
                    title="Net Funding (24h)" 
                    value={`+$${MOCK_HERO_STATS.netFunding.toLocaleString()}`} 
                    subtext="Deposits - Withdrawals" 
                    trend="+12%" trendUp={true} icon={ArrowRightLeft} 
                />
                <StatCard 
                    title="Total Variance (24h)" 
                    value={`$${MOCK_HERO_STATS.variance.toFixed(2)}`} 
                    subtext="Gateway & Execution diff" 
                    trend="Requires Review" trendUp={false} isWarning={MOCK_HERO_STATS.variance < 0} icon={AlertTriangle} 
                />
                <StatCard 
                    title="Network Gas Burn (24h)" 
                    value={`$${MOCK_HERO_STATS.gasBurn.toFixed(2)}`} 
                    subtext="TRX & ETH consumed" 
                    trend="Stable" trendUp={false} icon={Zap} 
                />
                
                {/* --- NEW: NOMMIA TRADE REVENUE WIDGET --- */}
                {(() => {
                    const BROKER_PROFIT_PER_LOT = 6.50;
                    // 🚨 Wires directly to your Live Supabase data
                    const estimatedGrossRevenue = liveMonthlyVolume * BROKER_PROFIT_PER_LOT;
                    
                    return (
                        <div className="bg-gradient-to-br from-green-900/40 to-slate-900 p-6 rounded-xl border border-green-500/30 relative overflow-hidden flex flex-col justify-between">
                            <div className="absolute top-0 right-0 bg-green-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-bl uppercase tracking-wider">Gross Profit</div>
                            <div>
                                <p className="text-green-400 text-xs font-medium mb-1 flex items-center gap-1.5"><TrendingUp size={14}/> Trade Revenue</p>
                                <h3 className="text-2xl font-bold text-white">${estimatedGrossRevenue.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
                            </div>
                            <div className="mt-2">
                                <span className="text-[10px] text-slate-500">Based on {liveMonthlyVolume.toLocaleString(undefined, {maximumFractionDigits: 2})} Lots @ ${BROKER_PROFIT_PER_LOT.toFixed(2)}/Lot</span>
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* TAB NAVIGATION */}
            <div className="flex space-x-2 border-b border-slate-800 mb-8 pb-px">
                <button
                    onClick={() => setActiveTab('treasury')}
                    className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'treasury' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                    <Server size={16} /> Treasury & Sweeps
                </button>
                <button
                    onClick={() => setActiveTab('funding')}
                    className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'funding' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                    <RefreshCw size={16} /> Funding Reconciliation
                </button>
                <button
                    onClick={() => { setActiveTab('withdrawals'); fetchWithdrawals(); }}
                    className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'withdrawals' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                    <Banknote size={16} /> Withdrawals
                    {withdrawalsList.filter(w => w.status === 'pending_approval' || w.status === 'pending').length > 0 && (
                        <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full border border-amber-500/30">
                            {withdrawalsList.filter(w => w.status === 'pending_approval' || w.status === 'pending').length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('execution')}
                    className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'execution' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                    <Activity size={16} /> Execution & Slippage
                </button>
                <button
                    onClick={() => setActiveTab('commissions')}
                    className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'commissions' ? 'border-amber-500 text-amber-500' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                >
                    <DollarSign size={16} /> Trading Revenue
                </button>
            </div>

            {/* TAB 1: TREASURY & SWEEPS */}
            {activeTab === 'treasury' && (
                <div className="animate-fade-in space-y-8">
                    {/* Control Panel */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 flex flex-col justify-between">
                            <div>
                                <h3 className="font-bold text-white mb-1 flex items-center gap-2"><Server size={18} className="text-blue-500"/> KMS Sweeper Engine</h3>
                                <p className="text-xs text-slate-400 mb-4">Status: <span className="text-green-400 font-bold">Online & Scanning</span></p>
                            </div>
                            <div className="space-y-3">
                                <div className="flex justify-between text-sm"><span className="text-slate-500">Unswept Liquidity</span><span className="text-white font-bold">$735.00</span></div>
                                <div className="flex justify-between text-sm"><span className="text-slate-500">Swept (30d)</span><span className="text-green-400 font-bold">$45,250.00</span></div>
                            </div>
                        </div>

                        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                            <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Zap size={18} className="text-amber-500"/> Corporate Gas Station</h3>
                            <div className="space-y-4">
                                <div>
                                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-400 font-bold">TRON (TRX)</span><span className="text-white">4,250 TRX</span></div>
                                    <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-amber-500 w-[60%]"></div></div>
                                </div>
                                <div>
                                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-400 font-bold">ETHEREUM (ETH)</span><span className="text-white">0.45 ETH</span></div>
                                    <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-blue-500 w-[30%]"></div></div>
                                </div>
                                <button className="w-full text-xs py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded transition-colors">Manage Gas Wallets</button>
                            </div>
                        </div>

                        <div className="bg-slate-900 rounded-2xl border border-red-500/30 p-6 flex flex-col justify-center items-center text-center relative overflow-hidden shadow-[0_0_15px_rgba(239,68,68,0.1)]">
                            <ShieldAlert size={32} className="text-red-500 mb-3" />
                            <h3 className="font-bold text-white mb-2">Emergency Liquidity Sweep</h3>
                            <p className="text-[10px] text-slate-400 mb-4 px-4">Ignore all minimum thresholds and instantly sweep all fragmented wallets to cold storage.</p>
                            <button 
                                onClick={handleRequestEmergencySweep}
                                disabled={isRequestingOtp}
                                className="bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-bold py-3 px-6 rounded-lg w-full transition-colors shadow-lg shadow-red-900/20"
                            >
                                {isRequestingOtp ? 'SENDING OTP...' : 'FORCE SWEEP NOW'}
                            </button>
                        </div>
                    </div>

                    {/* Fragmented Ledger */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                        <div className="p-6 border-b border-slate-800">
                            <h3 className="font-bold text-white">Fragmented Wallet Queue</h3>
                            <p className="text-xs text-slate-400 mt-1">Client wallets holding funds below the automatic sweep threshold.</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase">
                                    <tr>
                                        <th className="px-6 py-4">Client Email</th>
                                        <th className="px-6 py-4 text-center">Network</th>
                                        <th className="px-6 py-4 text-right">Current Balance</th>
                                        <th className="px-6 py-4 text-right">Sweep Threshold</th>
                                        <th className="px-6 py-4 text-right">Est. Gas</th>
                                        <th className="px-6 py-4 text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800 text-sm">
                                    {MOCK_FRAGMENTED_WALLETS.map(wallet => (
                                        <tr key={wallet.id} className="hover:bg-slate-800/50">
                                            <td className="px-6 py-4 font-medium text-white">{wallet.client}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`text-[10px] font-bold px-2 py-1 rounded border ${wallet.network === 'TRC20' ? 'bg-red-900/20 text-red-400 border-red-500/20' : 'bg-blue-900/20 text-blue-400 border-blue-500/20'}`}>
                                                    {wallet.network}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right font-bold text-green-400">${wallet.balance.toFixed(2)}</td>
                                            <td className="px-6 py-4 text-right text-slate-400">${wallet.threshold.toFixed(2)}</td>
                                            <td className="px-6 py-4 text-right text-slate-400">${wallet.estGas.toFixed(2)}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`text-xs font-bold ${wallet.status === 'Waiting' ? 'text-slate-500' : 'text-amber-500'}`}>{wallet.status}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: FUNDING RECONCILIATION */}
            {activeTab === 'funding' && (
                <div className="animate-fade-in space-y-6">
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center">
                            <div>
                                <h3 className="font-bold text-white flex items-center gap-2"><History className="text-amber-500" size={18}/> Deposit Reconciliation Ledger</h3>
                                <p className="text-xs text-slate-400 mt-1">Real-time matching of Gateway receipts vs CRM trading account credits.</p>
                            </div>
                            <button className="text-xs bg-slate-800 text-white px-4 py-2 rounded font-bold hover:bg-slate-700 transition-colors flex items-center gap-2">
                                <Download size={14}/> Export Report
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase">
                                    <tr>
                                        <th className="px-6 py-4">Date / Time</th>
                                        <th className="px-6 py-4">Client</th>
                                        <th className="px-6 py-4 text-center">Gateway</th>
                                        <th className="px-6 py-4 text-right">Received (Gateway)</th>
                                        <th className="px-6 py-4 text-right">Credited (CRM)</th>
                                        <th className="px-6 py-4 text-right">Variance</th>
                                        <th className="px-6 py-4 text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800 text-sm">
                                    {isLoadingFunding ? (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                                                <RefreshCw size={24} className="animate-spin text-amber-500 mx-auto mb-2" />
                                                Syncing live gateway data...
                                            </td>
                                        </tr>
                                    ) : reconciliationLog.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-12 text-center text-slate-500 italic">
                                                No recent deposits found or database table not yet initialized.
                                            </td>
                                        </tr>
                                    ) : (
                                        reconciliationLog.map(log => (
                                            <tr key={log.id} className={`transition-colors ${Number(log.variance) < 0 ? 'bg-red-950/10 hover:bg-red-950/20' : 'hover:bg-slate-800/50'}`}>
                                                <td className="px-6 py-4 text-slate-400 text-xs">
                                                    {new Date(log.created_at || log.date).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 font-medium text-white">{log.client_email || log.client}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-[10px] font-bold text-slate-400 bg-slate-950 px-2 py-1 border border-slate-800 rounded uppercase">
                                                        {log.method}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right font-mono text-slate-300">
                                                    ${Number(log.received_amount || log.received || 0).toFixed(2)}
                                                </td>
                                                <td className="px-6 py-4 text-right font-mono text-white">
                                                    ${Number(log.credited_amount || log.credited || 0).toFixed(2)}
                                                </td>
                                                <td className={`px-6 py-4 text-right font-bold font-mono ${Number(log.variance) < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                                                    {Number(log.variance) === 0 ? '-' : `$${Number(log.variance).toFixed(2)}`}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded border ${Number(log.variance) === 0 ? 'bg-green-900/20 text-green-400 border-green-500/20' : 'bg-red-900/20 text-red-400 border-red-500/20'}`}>
                                                        {log.status || (Number(log.variance) === 0 ? 'Matched' : 'Variance Alert')}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 4: TRADING COMMISSIONS (NOMMIA REVENUE) */}
            {activeTab === 'commissions' && (
                <div className="animate-fade-in space-y-6">
                    {/* Daily Revenue Table */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center">
                            <div>
                                <h3 className="font-bold text-white flex items-center gap-2"><DollarSign className="text-green-500" size={18}/> Daily Commission Earnings</h3>
                                <p className="text-xs text-slate-400 mt-1">Nommia's gross revenue from trading volume marked up at $6.50 per lot.</p>
                            </div>
                            <button className="text-xs bg-slate-800 text-white px-4 py-2 rounded font-bold hover:bg-slate-700 transition-colors flex items-center gap-2">
                                <Download size={14}/> Export CSV
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase">
                                    <tr>
                                        <th className="px-6 py-4">Date</th>
                                        <th className="px-6 py-4 text-right">Total Volume (Lots)</th>
                                        <th className="px-6 py-4 text-right">Company Markup</th>
                                        <th className="px-6 py-4 text-right">Gross Revenue</th>
                                        <th className="px-6 py-4 text-right">IB Payouts</th>
                                        <th className="px-6 py-4 text-right">Net Profit</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800 text-sm">
                                    {dailyRevenueData.length === 0 ? (
                                        <tr><td colSpan="6" className="p-8 text-center text-slate-500 italic">No trading revenue generated in the last 30 days.</td></tr>
                                    ) : (
                                        dailyRevenueData.map((day, idx) => (
                                            <tr key={idx} className="hover:bg-slate-800/50 transition-colors">
                                                <td className="px-6 py-4 font-medium text-white">{day.date}</td>
                                                <td className="px-6 py-4 text-right text-slate-300 font-mono">{day.volume.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                                                <td className="px-6 py-4 text-right text-slate-400 font-mono">${day.rate.toFixed(2)}/Lot</td>
                                                <td className="px-6 py-4 text-right text-green-400 font-bold font-mono">${day.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                                <td className="px-6 py-4 text-right text-red-400 font-mono">-${day.partnerPayouts.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                                <td className="px-6 py-4 text-right text-white font-bold font-mono">${day.net.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    
                    {/* Revenue by Asset Class */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                        <div className="p-6 border-b border-slate-800">
                            <h3 className="font-bold text-white flex items-center gap-2"><Activity className="text-blue-500" size={18}/> Revenue by Asset Class (MTD)</h3>
                            <p className="text-xs text-slate-400 mt-1">Breakdown of Nommia's spread/commission earnings by instrument type.</p>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                                <p className="text-xs text-slate-500 uppercase font-bold mb-1">Forex (FX)</p>
                                <p className="text-xl font-bold text-white">$145,200.00</p>
                                <div className="mt-2 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-blue-500 w-[65%]"></div></div>
                                <p className="text-[10px] text-slate-500 mt-2">65% of Total Volume</p>
                            </div>
                            <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                                <p className="text-xs text-slate-500 uppercase font-bold mb-1">Metals (XAU/XAG)</p>
                                <p className="text-xl font-bold text-white">$58,150.00</p>
                                <div className="mt-2 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-amber-500 w-[25%]"></div></div>
                                <p className="text-[10px] text-slate-500 mt-2">25% of Total Volume</p>
                            </div>
                            <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                                <p className="text-xs text-slate-500 uppercase font-bold mb-1">Indices & Crypto</p>
                                <p className="text-xl font-bold text-white">$22,350.00</p>
                                <div className="mt-2 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-purple-500 w-[10%]"></div></div>
                                <p className="text-[10px] text-slate-500 mt-2">10% of Total Volume</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 3: EXECUTION & SLIPPAGE */}
            {activeTab === 'execution' && (
                <div className="animate-fade-in space-y-6">
                    
                    {/* DUAL CSV UPLOAD ZONE */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Box A: Client Log */}
                        <div 
                            onClick={() => clientFileInputRef.current.click()}
                            className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center text-center transition-all cursor-pointer ${clientDataRaw ? 'bg-green-900/10 border-green-500/30 hover:bg-green-900/20' : 'bg-slate-950 border-slate-700 hover:bg-slate-900 hover:border-amber-500/50'}`}
                        >
                            <input type="file" accept=".csv" ref={clientFileInputRef} onChange={handleClientFileChange} className="hidden" />
                            {clientDataRaw ? (
                                <>
                                    <CheckCircle2 size={32} className="text-green-500 mb-2" />
                                    <h3 className="font-bold text-green-400">Client Log Loaded</h3>
                                    <p className="text-xs text-slate-400 mt-1">{clientDataRaw.length} rows detected.</p>
                                </>
                            ) : (
                                <>
                                    <UploadCloud size={32} className="text-amber-500 mb-2" />
                                    <h3 className="font-bold text-white">1. Client Platform Log</h3>
                                    <p className="text-xs text-slate-400 mt-1">Upload the CSV export containing the client's requested prices.</p>
                                </>
                            )}
                        </div>

                        {/* Box B: LP Log */}
                        <div 
                            onClick={() => lpFileInputRef.current.click()}
                            className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center text-center transition-all cursor-pointer ${lpDataRaw ? 'bg-green-900/10 border-green-500/30 hover:bg-green-900/20' : 'bg-slate-950 border-slate-700 hover:bg-slate-900 hover:border-amber-500/50'}`}
                        >
                            <input type="file" accept=".csv" ref={lpFileInputRef} onChange={handleLpFileChange} className="hidden" />
                            {lpDataRaw ? (
                                <>
                                    <CheckCircle2 size={32} className="text-green-500 mb-2" />
                                    <h3 className="font-bold text-green-400">LP Log Loaded</h3>
                                    <p className="text-xs text-slate-400 mt-1">{lpDataRaw.length} rows detected.</p>
                                </>
                            ) : (
                                <>
                                    <FileText size={32} className="text-blue-500 mb-2" />
                                    <h3 className="font-bold text-white">2. LP Execution Log</h3>
                                    <p className="text-xs text-slate-400 mt-1">Upload the CSV export containing the actual Liquidity Provider fills.</p>
                                </>
                            )}
                        </div>

                    </div>

                    {/* Execute Match Button */}
                    <div className="flex justify-center">
                        <button 
                            onClick={runVarianceAnalysis}
                            disabled={!clientDataRaw || !lpDataRaw || isAnalyzing}
                            className="bg-amber-500 disabled:bg-slate-800 text-slate-900 disabled:text-slate-500 font-bold py-3 px-12 rounded-lg transition-colors shadow-lg shadow-amber-900/20 flex items-center gap-2"
                        >
                            {isAnalyzing ? <RefreshCw size={18} className="animate-spin"/> : <Activity size={18} />}
                            {isAnalyzing ? 'Cross-Referencing Logs...' : 'Run Variance Analysis'}
                        </button>
                    </div>

                    {/* Stats Row (Shows after match) */}
                    {slippageLog.length > 0 && (
                        <div className="grid grid-cols-4 gap-4 animate-fade-in">
                            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                                <p className="text-xs text-slate-500 uppercase font-bold mb-1">Matched Trades</p>
                                <p className="text-xl font-bold text-white">{executionStats.totalTrades}</p>
                            </div>
                            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                                <p className="text-xs text-slate-500 uppercase font-bold mb-1">Net PnL Impact</p>
                                <p className={`text-xl font-bold ${executionStats.netPnL > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {executionStats.netPnL > 0 ? '+' : ''}${executionStats.netPnL.toFixed(2)}
                                </p>
                            </div>
                            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                                <p className="text-xs text-slate-500 uppercase font-bold mb-1">Positive Slippage</p>
                                <p className="text-xl font-bold text-green-400">{executionStats.positiveTrades} Trades</p>
                            </div>
                            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                                <p className="text-xs text-slate-500 uppercase font-bold mb-1">Negative Slippage</p>
                                <p className="text-xl font-bold text-red-400">{executionStats.negativeTrades} Trades</p>
                            </div>
                        </div>
                    )}

                    {/* Slippage Ledger */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center">
                            <h3 className="font-bold text-white">Execution Variance Report</h3>
                            <button disabled={slippageLog.length === 0} className="text-xs bg-slate-800 text-white px-4 py-2 rounded font-bold hover:bg-slate-700 transition-colors disabled:opacity-50">
                                Export Results
                            </button>
                        </div>
                        <div className="overflow-x-auto max-h-[600px]">
                            <table className="w-full text-left relative">
                                <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase sticky top-0 z-10 shadow-md">
                                    <tr>
                                        <th className="px-6 py-4">Ticket</th>
                                        <th className="px-6 py-4">Client</th>
                                        <th className="px-6 py-4">Asset</th>
                                        <th className="px-6 py-4 text-center">Volume</th>
                                        <th className="px-6 py-4 text-right">Client Price</th>
                                        <th className="px-6 py-4 text-right">LP Price</th>
                                        <th className="px-6 py-4 text-right">Variance (Pips)</th>
                                        <th className="px-6 py-4 text-right">Broker PnL Impact</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800 text-sm">
                                    {isAnalyzing ? (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-12 text-center text-amber-500">
                                                <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                                                Cross-referencing execution logs...
                                            </td>
                                        </tr>
                                    ) : slippageLog.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-12 text-center text-slate-500 italic">
                                                {clientDataRaw && lpDataRaw 
                                                    ? "Analysis run, but 0 matching tickets were found. Check your CSV column headers." 
                                                    : "Upload both CSV files above and run the analysis to view results."}
                                            </td>
                                        </tr>
                                    ) : (
                                        slippageLog.map(trade => (
                                            <tr key={trade.id} className="hover:bg-slate-800/50 transition-colors">
                                                <td className="px-6 py-4 font-mono text-xs text-slate-500 flex flex-col gap-1">
                                                    <span>C: {trade.id}</span>
                                                    <span className="text-[10px] text-slate-600">LP: {trade.lpId}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="font-bold text-white block">{trade.client}</span>
                                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded w-fit uppercase mt-1 inline-block ${trade.direction === 'BUY' ? 'bg-green-900/30 text-green-400 border border-green-500/20' : 'bg-red-900/30 text-red-400 border border-red-500/20'}`}>
                                                        {trade.direction}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 font-bold text-white">{trade.asset}</td>
                                                <td className="px-6 py-4 text-center text-slate-300">{trade.volume}</td>
                                                <td className="px-6 py-4 text-right font-mono text-amber-500">{trade.clientPrice.toFixed(5)}</td>
                                                <td className="px-6 py-4 text-right font-mono text-blue-400">{trade.lpPrice.toFixed(5)}</td>
                                                <td className={`px-6 py-4 text-right font-bold ${trade.variancePips > 0 ? 'text-green-400' : trade.variancePips < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                                                    {trade.variancePips > 0 ? '+' : ''}{trade.variancePips}
                                                </td>
                                                <td className={`px-6 py-4 text-right font-bold ${trade.pnlImpact > 0 ? 'text-green-400' : trade.pnlImpact < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                                                    {trade.pnlImpact > 0 ? '+' : ''}${Math.abs(trade.pnlImpact).toFixed(2)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: WITHDRAWALS HUB */}
            {activeTab === 'withdrawals' && (() => {
                const fiatList = withdrawalsList.filter(isFiatItem);
                const cryptoList = withdrawalsList.filter(isCryptoItem);
                const currentCategoryList = withdrawalCategory === 'fiat' ? fiatList : cryptoList;

                const pendingFiat = fiatList.filter(w => w.status === 'pending_approval' || w.status === 'pending');
                const pendingCrypto = cryptoList.filter(w => w.status === 'pending_approval' || w.status === 'pending');

                const completedFiat = fiatList.filter(w => w.status === 'completed');
                const completedCrypto = cryptoList.filter(w => w.status === 'completed');

                return (
                <div className="animate-fade-in space-y-6">
                    {/* SUB-TAB TOGGLE: BANK ACCOUNT (FIAT) VS CRYPTO */}
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-3 sm:p-4 rounded-2xl">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-slate-950 p-1.5 rounded-xl border border-slate-800 w-full lg:w-auto">
                            <button
                                onClick={() => { setWithdrawalCategory('fiat'); setWithdrawalsFilter('all'); }}
                                className={`flex-1 sm:flex-initial px-3.5 sm:px-5 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-between sm:justify-start gap-2.5 ${
                                    withdrawalCategory === 'fiat'
                                        ? 'bg-amber-500 text-slate-950 shadow-md font-extrabold'
                                        : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                <span className="flex items-center gap-2">
                                    <Building2 size={15} />
                                    <span>Bank Account (Fiat / NGN)</span>
                                </span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${
                                    withdrawalCategory === 'fiat' ? 'bg-black/20 text-slate-950' : 'bg-slate-800 text-amber-400'
                                }`}>
                                    {pendingFiat.length} Pending
                                </span>
                            </button>

                            <button
                                onClick={() => { setWithdrawalCategory('crypto'); setWithdrawalsFilter('all'); }}
                                className={`flex-1 sm:flex-initial px-3.5 sm:px-5 py-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-between sm:justify-start gap-2.5 ${
                                    withdrawalCategory === 'crypto'
                                        ? 'bg-amber-500 text-slate-950 shadow-md font-extrabold'
                                        : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                <span className="flex items-center gap-2">
                                    <Wallet size={15} />
                                    <span>Crypto Withdrawals</span>
                                </span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${
                                    withdrawalCategory === 'crypto' ? 'bg-black/20 text-slate-950' : 'bg-slate-800 text-slate-400'
                                }`}>
                                    {pendingCrypto.length} Pending
                                </span>
                            </button>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-slate-400 px-1">
                            <span>Total Historical: <strong className="text-white font-mono">{withdrawalsList.length}</strong></span>
                            <span>•</span>
                            <span>Bank/Fiat: <strong className="text-amber-400 font-mono">{fiatList.length}</strong></span>
                            <span>•</span>
                            <span>Crypto: <strong className="text-blue-400 font-mono">{cryptoList.length}</strong></span>
                        </div>
                    </div>

                    {/* Top Stat Cards (Category Specific) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4">
                        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-5">
                            <p className="text-xs text-slate-500 uppercase font-bold mb-1">
                                {withdrawalCategory === 'fiat' ? 'Pending Bank Approvals' : 'Pending Crypto Approvals'}
                            </p>
                            <div className="flex items-baseline gap-2">
                                <h4 className="text-2xl font-bold text-amber-400">
                                    {withdrawalCategory === 'fiat' ? pendingFiat.length : pendingCrypto.length}
                                </h4>
                                <span className="text-xs text-slate-400">
                                    (${((withdrawalCategory === 'fiat' ? pendingFiat : pendingCrypto).reduce((acc, curr) => acc + (parseFloat(curr.amount_usd) || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                </span>
                            </div>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-5">
                            <p className="text-xs text-slate-500 uppercase font-bold mb-1">
                                {withdrawalCategory === 'fiat' ? 'Completed Bank Payouts' : 'Completed Crypto Payouts'}
                            </p>
                            <div className="flex items-baseline gap-2">
                                <h4 className="text-2xl font-bold text-green-400">
                                    {withdrawalCategory === 'fiat' ? completedFiat.length : completedCrypto.length}
                                </h4>
                                <span className="text-xs text-slate-400">
                                    (${((withdrawalCategory === 'fiat' ? completedFiat : completedCrypto).reduce((acc, curr) => acc + (parseFloat(curr.amount_usd) || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                </span>
                            </div>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-5">
                            <p className="text-xs text-slate-500 uppercase font-bold mb-1">
                                {withdrawalCategory === 'fiat' ? 'Total NGN Disbursed' : 'Completed Crypto Volume'}
                            </p>
                            <h4 className="text-2xl font-bold text-white font-mono">
                                {withdrawalCategory === 'fiat' 
                                    ? `₦${completedFiat.reduce((acc, curr) => {
                                        const km = getKorapayMeta(curr);
                                        return acc + (parseFloat(curr.metadata?.payout_amount_ngn || curr.amount_ngn || km.payout_amount_ngn) || 0);
                                    }, 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                                    : `$${completedCrypto.reduce((acc, curr) => acc + (parseFloat(curr.amount_usd) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                }
                            </h4>
                        </div>

                        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-5 flex items-center justify-between">
                            <div>
                                <p className="text-xs text-slate-500 uppercase font-bold mb-1">Channel Status</p>
                                <p className="text-xs text-green-400 font-bold flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                    {withdrawalCategory === 'fiat' ? 'Korapay Rails Active' : 'Treasury Node Active'}
                                </p>
                            </div>
                            <span className="text-xs px-2.5 py-1 rounded-full bg-green-500/10 text-green-400 border border-green-500/20 font-bold">
                                Healthy
                            </span>
                        </div>
                    </div>

                    {/* Filter and Search Bar */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3.5 sm:p-4 flex flex-col lg:flex-row gap-3 sm:gap-4 items-stretch lg:items-center justify-between">
                        {/* Status Filter Pills */}
                        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 lg:pb-0 w-full lg:w-auto -mx-1 px-1 scrollbar-none">
                            {['all', 'pending_approval', 'processing', 'completed', 'declined'].map(st => (
                                <button
                                    key={st}
                                    onClick={() => setWithdrawalsFilter(st)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-colors whitespace-nowrap shrink-0 ${
                                        withdrawalsFilter === st 
                                            ? 'bg-amber-500 text-slate-900 font-extrabold shadow-sm' 
                                            : 'bg-slate-800 text-slate-400 hover:text-white'
                                    }`}
                                >
                                    {st === 'all' ? 'All Requests' : st.replace('_', ' ')}
                                </button>
                            ))}
                        </div>

                        {/* Right Action Group: Date Cutoff Filter Toggle + Refresh + Search */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5 w-full lg:w-auto">
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const nextVal = !showLegacyWithdrawals;
                                        setShowLegacyWithdrawals(nextVal);
                                        fetchWithdrawals(nextVal);
                                    }}
                                    className={`flex-1 sm:flex-initial px-3 py-2 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 whitespace-nowrap shadow-sm ${
                                        showLegacyWithdrawals
                                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/30'
                                            : 'bg-slate-950 text-slate-300 border-slate-800 hover:text-white hover:bg-slate-800'
                                    }`}
                                    title={showLegacyWithdrawals ? "Showing all historical records including legacy settled transactions" : "Filter active: Showing transactions from Oct 23 onwards only"}
                                >
                                    <Filter size={13} className={showLegacyWithdrawals ? 'text-amber-400' : 'text-slate-400'} />
                                    <span>{showLegacyWithdrawals ? 'Showing All History' : 'Live Queue (Oct 23+)'}</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => fetchWithdrawals()}
                                    disabled={withdrawalsLoading}
                                    className="p-2 sm:p-2.5 bg-slate-950 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl transition-colors border border-slate-800 shadow-sm flex items-center justify-center shrink-0"
                                    title="Refresh live queue"
                                >
                                    <RefreshCw size={15} className={withdrawalsLoading ? 'animate-spin text-amber-400' : ''} />
                                </button>
                            </div>

                            <div className="relative w-full sm:w-64 md:w-72">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                    type="text"
                                    placeholder={withdrawalCategory === 'fiat' ? "Search bank, email, NUBAN..." : "Search wallet, email, ref..."}
                                    value={withdrawalsSearch}
                                    onChange={(e) => setWithdrawalsSearch(e.target.value)}
                                    className="w-full bg-slate-950 border border-slate-800 text-white text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-amber-500/50 shadow-inner"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Withdrawals Table */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
                        <div className="overflow-x-auto scrollbar-thin">
                            <table className="w-full text-left text-xs min-w-[860px] lg:min-w-[960px]">
                                <thead className="bg-slate-950 text-slate-400 font-bold uppercase border-b border-slate-800">
                                    <tr>
                                        <th className="px-5 py-4">Date / Ref</th>
                                        <th className="px-5 py-4">Client</th>
                                        <th className="px-5 py-4">MT5 Account</th>
                                        {withdrawalCategory === 'fiat' ? (
                                            <>
                                                <th className="px-5 py-4">Destination Bank Details</th>
                                                <th className="px-5 py-4 text-right">Debit (USD)</th>
                                                <th className="px-5 py-4 text-right">Bank Payout (NGN)</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="px-5 py-4">Asset / Network</th>
                                                <th className="px-5 py-4">Destination Wallet Address</th>
                                                <th className="px-5 py-4 text-right">Amount (USD)</th>
                                            </>
                                        )}
                                        <th className="px-5 py-4 text-center">Status</th>
                                        <th className="px-5 py-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800">
                                    {withdrawalsLoading ? (
                                        <tr>
                                            <td colSpan={withdrawalCategory === 'fiat' ? 8 : 7} className="px-5 py-12 text-center text-slate-500 animate-pulse">
                                                Loading withdrawal records...
                                            </td>
                                        </tr>
                                    ) : currentCategoryList.filter(w => {
                                        if (withdrawalsFilter !== 'all') {
                                            if (withdrawalsFilter === 'pending_approval' && (w.status !== 'pending_approval' && w.status !== 'pending')) return false;
                                            if (withdrawalsFilter !== 'pending_approval' && w.status !== withdrawalsFilter) return false;
                                        }
                                        if (withdrawalsSearch.trim()) {
                                            const s = withdrawalsSearch.toLowerCase();
                                            const matchEmail = (w.user_email || '').toLowerCase().includes(s);
                                            const matchRef = (w.reference || '').toLowerCase().includes(s);
                                            const matchAcc = (w.xvalley_account_id || '').toLowerCase().includes(s);
                                            const matchDest = (w.destination || '').toLowerCase().includes(s);
                                            if (!matchEmail && !matchRef && !matchAcc && !matchDest) return false;
                                        }
                                        return true;
                                    }).length === 0 ? (
                                        <tr>
                                            <td colSpan={withdrawalCategory === 'fiat' ? 8 : 7} className="px-5 py-12 text-center text-slate-500">
                                                {withdrawalCategory === 'fiat' ? 'No Bank Account withdrawal requests found in this view.' : 'No Crypto withdrawal requests found in this view.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        currentCategoryList.filter(w => {
                                            if (withdrawalsFilter !== 'all') {
                                                if (withdrawalsFilter === 'pending_approval' && (w.status !== 'pending_approval' && w.status !== 'pending')) return false;
                                                if (withdrawalsFilter !== 'pending_approval' && w.status !== withdrawalsFilter) return false;
                                            }
                                            if (withdrawalsSearch.trim()) {
                                                const s = withdrawalsSearch.toLowerCase();
                                                const matchEmail = (w.user_email || '').toLowerCase().includes(s);
                                                const matchRef = (w.reference || '').toLowerCase().includes(s);
                                                const matchAcc = (w.xvalley_account_id || '').toLowerCase().includes(s);
                                                const matchDest = (w.destination || '').toLowerCase().includes(s);
                                                if (!matchEmail && !matchRef && !matchAcc && !matchDest) return false;
                                            }
                                            return true;
                                        }).map((item) => {
                                            const isPending = item.status === 'pending_approval' || item.status === 'pending';
                                            const isProcessing = item.status === 'processing';
                                            const isCompleted = item.status === 'completed';
                                            const isDeclined = item.status === 'declined' || item.status === 'rejected_by_broker';
                                            const meta = isKorapayItem(item) ? getKorapayMeta(item) : (item.metadata || {});

                                            return (
                                                <tr key={item.id} className="hover:bg-slate-800/40 transition-colors">
                                                    <td className="px-5 py-4">
                                                        <span className="font-mono text-slate-400 block">{new Date(item.created_at).toLocaleDateString()}</span>
                                                        <span className="font-mono text-[10px] text-slate-500 block truncate max-w-[120px]" title={item.reference}>
                                                            {item.reference}
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-4">
                                                        <span className="font-bold text-white block truncate max-w-[180px]" title={item.user_email}>
                                                            {item.user_email}
                                                        </span>
                                                        {meta.country === 'NG' && (
                                                            <span className="inline-block mt-0.5 text-[9px] bg-green-500/10 text-green-400 border border-green-500/20 px-1.5 py-0.2 rounded font-bold">
                                                                Nigeria
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-5 py-4">
                                                        <span className="font-mono text-slate-300 block">#{item.xvalley_account_id || 'N/A'}</span>
                                                        <span className="text-[10px] text-slate-500 block">{item.gateway || (withdrawalCategory === 'fiat' ? 'Bank Account' : 'Crypto')}</span>
                                                    </td>

                                                    {withdrawalCategory === 'fiat' ? (
                                                        <>
                                                            <td className="px-5 py-4">
                                                                {meta.bank_name ? (
                                                                    <div>
                                                                        <span className="font-bold text-white block">{meta.bank_name}</span>
                                                                        <span className="font-mono text-[11px] text-amber-300 block">{meta.account_number}</span>
                                                                        <span className="text-[10px] text-slate-400 block truncate max-w-[160px]" title={meta.account_name}>
                                                                            {meta.account_name}
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <span className="font-mono text-slate-400 truncate max-w-[200px] block" title={item.destination}>
                                                                        {item.destination}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-5 py-4 text-right font-mono font-bold text-white">
                                                                ${parseFloat(item.amount_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="px-5 py-4 text-right font-mono font-bold text-amber-400">
                                                                {meta.payout_amount_ngn
                                                                    ? `₦${parseFloat(meta.payout_amount_ngn).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                                    : '—'}
                                                            </td>
                                                        </>
                                                    ) : (
                                                        (() => {
                                                            const parsed = parseCryptoDestination(item.destination, item.gateway);
                                                            return (
                                                                <>
                                                                    <td className="px-5 py-4">
                                                                        <div className="space-y-1">
                                                                            <span className="font-bold text-white block leading-snug">
                                                                                {item.gateway || 'Crypto'}
                                                                            </span>
                                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                                {parsed.network && (
                                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 shadow-sm">
                                                                                        {parsed.network}
                                                                                    </span>
                                                                                )}
                                                                                {parsed.walletType && (
                                                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                                                                                        parsed.isInternal
                                                                                            ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                                                                            : 'bg-slate-700/50 text-slate-300 border-slate-600'
                                                                                    }`}>
                                                                                        {parsed.walletType}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-5 py-4">
                                                                        <div className="flex items-center gap-2">
                                                                            <span 
                                                                                className="font-mono text-slate-200 text-xs truncate max-w-[220px] bg-slate-900/60 px-2 py-1 rounded border border-slate-800 select-all" 
                                                                                title={parsed.address || item.destination}
                                                                            >
                                                                                {parsed.address || item.destination}
                                                                            </span>
                                                                            {(parsed.address || item.destination) && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleCopyAddress(parsed.address || item.destination, item.id)}
                                                                                    className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-amber-400 transition-colors border border-transparent hover:border-slate-700"
                                                                                    title="Copy Address to Clipboard"
                                                                                >
                                                                                    {copiedId === item.id ? (
                                                                                        <Check size={14} className="text-green-400" />
                                                                                    ) : (
                                                                                        <Copy size={14} />
                                                                                    )}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-5 py-4 text-right font-mono font-bold text-white">
                                                                        ${parseFloat(item.amount_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </td>
                                                                </>
                                                            );
                                                        })()
                                                    )}

                                                    <td className="px-5 py-4 text-center">
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase inline-flex items-center gap-1 ${
                                                            isPending ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                                                            isProcessing ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20 animate-pulse' :
                                                            isCompleted ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                                                            'bg-red-500/10 text-red-400 border border-red-500/20'
                                                        }`}>
                                                            {isPending && <Clock size={10} />}
                                                            {isProcessing && <RefreshCw size={10} className="animate-spin" />}
                                                            {isCompleted && <Check size={10} />}
                                                            {isDeclined && <X size={10} />}
                                                            {item.status.replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-1.5">
                                                            {isPending && (
                                                                <>
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedWithdrawal(item);
                                                                            setActionType('approve');
                                                                            setActionModalOpen(true);
                                                                            setActionFeedback(null);
                                                                        }}
                                                                        className="bg-green-500 hover:bg-green-400 text-slate-950 font-bold px-3 py-1.5 rounded-lg text-xs transition-colors shadow-sm"
                                                                    >
                                                                        Approve
                                                                    </button>
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedWithdrawal(item);
                                                                            setActionType('decline');
                                                                            setActionModalOpen(true);
                                                                            setActionFeedback(null);
                                                                        }}
                                                                        className="bg-slate-800 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-slate-700 font-bold px-2.5 py-1.5 rounded-lg text-xs transition-colors"
                                                                    >
                                                                        Decline
                                                                    </button>
                                                                </>
                                                            )}
                                                            {isProcessing && (
                                                                <span className="text-[10px] text-blue-400 font-mono">Disbursing...</span>
                                                            )}
                                                            {isCompleted && (
                                                                <span className="text-[10px] text-slate-500 font-mono truncate max-w-[100px] block" title={item.payout_reference}>
                                                                    {item.payout_reference || 'Disbursed'}
                                                                </span>
                                                            )}
                                                            {isDeclined && (
                                                                <span className="text-[10px] text-red-400 block truncate max-w-[120px]" title={item.broker_error}>
                                                                    {item.broker_error || 'Declined'}
                                                                </span>
                                                            )}
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedWithdrawal(item);
                                                                    setActionType('delete');
                                                                    setActionModalOpen(true);
                                                                    setActionFeedback(null);
                                                                }}
                                                                className="p-1.5 bg-slate-800 hover:bg-red-500/20 text-slate-500 hover:text-red-400 border border-slate-700 rounded-lg text-xs transition-colors ml-1"
                                                                title="Delete from CRM and XValley"
                                                            >
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* MODAL: 1-Click Approve / Decline Confirmation */}
                    {actionModalOpen && selectedWithdrawal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
                            <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full p-4 sm:p-6 space-y-4 sm:space-y-5 shadow-2xl relative max-h-[92vh] overflow-y-auto">
                                <button
                                    onClick={() => setActionModalOpen(false)}
                                    disabled={actionProcessing}
                                    className="absolute right-4 top-4 text-slate-400 hover:text-white"
                                >
                                    <X size={18} />
                                </button>

                                {actionType === 'approve' ? (
                                    <>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 border border-green-500/30">
                                                <Banknote size={20} />
                                            </div>
                                            <div>
                                                <h3 className="text-base font-bold text-white">
                                                    {isKorapayItem(selectedWithdrawal) 
                                                        ? '1-Click Korapay Payout Approval' 
                                                        : isManualBankItem(selectedWithdrawal)
                                                        ? 'Manual Bank Wire Approval'
                                                        : 'Approve Crypto Withdrawal'}
                                                </h3>
                                                <p className="text-xs text-slate-400">
                                                    {isKorapayItem(selectedWithdrawal) 
                                                        ? 'Execute automated disbursement via Korapay rails (NGN)'
                                                        : isManualBankItem(selectedWithdrawal)
                                                        ? 'Confirm bank wire disbursement to client account'
                                                        : 'Confirm on-chain disbursement to client address'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-2.5 text-xs">
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Client:</span>
                                                <span className="text-white font-bold">{selectedWithdrawal.user_email}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">MT5 Account:</span>
                                                <span className="text-white font-mono">#{selectedWithdrawal.xvalley_account_id}</span>
                                            </div>

                                            {isKorapayItem(selectedWithdrawal) ? (
                                                (() => {
                                                    const km = getKorapayMeta(selectedWithdrawal);
                                                    return (
                                                        <>
                                                            <div className="flex justify-between">
                                                                <span className="text-slate-500">Bank Name:</span>
                                                                <span className="text-white font-bold">{km.bank_name || 'N/A'}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-slate-500">Account Number (NUBAN):</span>
                                                                <span className="text-amber-400 font-mono font-bold">{km.account_number || 'N/A'}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-slate-500">Verified Account Holder:</span>
                                                                <span className="text-green-400 font-bold">{km.account_name || 'N/A'}</span>
                                                            </div>
                                                        </>
                                                    );
                                                })()
                                            ) : isManualBankItem(selectedWithdrawal) ? (
                                                <>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-500">Destination / Bank:</span>
                                                        <span className="text-white font-bold">{selectedWithdrawal.destination || selectedWithdrawal.gateway || 'Bank Wire'}</span>
                                                    </div>
                                                    <div className="pt-1.5 border-t border-slate-800">
                                                        <label className="block text-slate-400 text-[10px] mb-1 font-medium">Bank Wire Ref / Tracking # (Optional):</label>
                                                        <input 
                                                            type="text" 
                                                            placeholder="e.g. FED-WIRE-98214 or Bank Receipt #" 
                                                            value={wireReference} 
                                                            onChange={(e) => setWireReference(e.target.value)} 
                                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-xs font-mono focus:outline-none focus:border-blue-500"
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                (() => {
                                                    const parsedModal = parseCryptoDestination(selectedWithdrawal.destination, selectedWithdrawal.gateway);
                                                    return (
                                                        <div className="space-y-2">
                                                            <div className="flex justify-between items-center text-xs">
                                                                <span className="text-slate-500">Network / Chain:</span>
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="text-amber-400 font-bold bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                                                                        {parsedModal.network || 'Crypto'}
                                                                    </span>
                                                                    {parsedModal.walletType && (
                                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                                                                            parsedModal.isInternal 
                                                                                ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' 
                                                                                : 'bg-slate-700/50 text-slate-300 border-slate-600'
                                                                        }`}>
                                                                            {parsedModal.walletType}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex justify-between items-center text-xs">
                                                                <span className="text-slate-500">Destination Address:</span>
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="text-white font-mono text-xs select-all truncate max-w-[210px]" title={parsedModal.address}>
                                                                        {parsedModal.address || selectedWithdrawal.destination}
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCopyAddress(parsedModal.address || selectedWithdrawal.destination, 'modal')}
                                                                        className="p-1 hover:bg-slate-700/80 rounded text-slate-400 hover:text-amber-400 transition-colors flex items-center gap-1"
                                                                        title="Copy Wallet Address"
                                                                    >
                                                                        {copiedId === 'modal' ? (
                                                                            <span className="flex items-center text-[10px] text-green-400 font-bold gap-0.5">
                                                                                <Check size={12} /> Copied
                                                                            </span>
                                                                        ) : (
                                                                            <Copy size={13} />
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()
                                            )}

                                            <div className="border-t border-slate-800 pt-2 flex justify-between items-baseline">
                                                <span className="text-slate-400 font-bold">USD Debit:</span>
                                                <span className="text-white font-bold font-mono text-sm">${selectedWithdrawal.amount_usd}</span>
                                            </div>

                                            {isFiatItem(selectedWithdrawal) && (() => {
                                                const km = getKorapayMeta(selectedWithdrawal);
                                                return km.payout_amount_ngn ? (
                                                    <div className="flex justify-between items-baseline">
                                                        <span className="text-amber-400 font-bold">Korapay Payout:</span>
                                                        <span className="text-amber-400 font-bold font-mono text-base">
                                                            ₦{parseFloat(km.payout_amount_ngn).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} NGN
                                                        </span>
                                                    </div>
                                                ) : null;
                                            })()}
                                        </div>

                                        {actionFeedback && (
                                            <div className={`p-3 rounded-xl text-xs font-bold ${actionFeedback.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
                                                {actionFeedback.message}
                                            </div>
                                        )}

                                        <p className="text-[11px] text-slate-500 leading-relaxed">
                                            {isKorapayItem(selectedWithdrawal) 
                                                ? "Clicking confirm will dispatch funds from Nommia's Korapay treasury account directly to the beneficiary's local Nigerian bank account."
                                                : isManualBankItem(selectedWithdrawal)
                                                ? "Clicking confirm marks this bank wire as disbursed in Nommia and updates XValley to Completed."
                                                : "Clicking confirm marks this crypto payout as approved and updates XValley to Completed."}
                                        </p>

                                        <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 pt-2">
                                            <button
                                                onClick={() => setActionModalOpen(false)}
                                                disabled={actionProcessing}
                                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 rounded-xl transition-colors text-xs"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleExecuteApproval}
                                                disabled={actionProcessing}
                                                className="flex-1 bg-green-500 hover:bg-green-400 text-slate-950 font-bold py-2.5 rounded-xl transition-colors text-xs flex items-center justify-center gap-1.5 shadow-lg shadow-green-900/30"
                                            >
                                                {actionProcessing ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                                                {actionProcessing ? "Processing..." : isKorapayItem(selectedWithdrawal) ? "Confirm & Disburse (Korapay)" : isManualBankItem(selectedWithdrawal) ? "Confirm Wire Sent" : "Confirm Crypto Sent"}
                                            </button>
                                        </div>
                                    </>
                                ) : actionType === 'delete' ? (
                                    <>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 border border-red-500/30">
                                                <Trash2 size={20} />
                                            </div>
                                            <div>
                                                <h3 className="text-base font-bold text-white">Delete Withdrawal Record</h3>
                                                <p className="text-xs text-slate-400">Permanently delete from CRM and XValley</p>
                                            </div>
                                        </div>

                                        <div className="bg-slate-950 border border-slate-800 rounded-xl p-3.5 space-y-2 text-xs">
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Reference:</span>
                                                <span className="text-white font-mono">{selectedWithdrawal?.reference || 'N/A'}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Client:</span>
                                                <span className="text-white font-medium">{selectedWithdrawal?.user_email || 'N/A'}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Trading Account:</span>
                                                <span className="text-white font-mono">#{selectedWithdrawal?.xvalley_account_id || 'N/A'}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Amount:</span>
                                                <span className="text-white font-mono font-bold">${selectedWithdrawal?.amount_usd}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Current Status:</span>
                                                <span className="capitalize font-mono text-amber-400 font-bold">{selectedWithdrawal?.status}</span>
                                            </div>
                                        </div>

                                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-xs text-red-300 leading-relaxed">
                                            <p className="font-bold mb-1 flex items-center gap-1.5 text-red-400">
                                                <AlertTriangle size={14} /> Warning: Irreversible Action
                                            </p>
                                            This action will mark the withdrawal transaction as <strong className="text-white">Deleted (State 5)</strong> in XValley and remove it from the active CRM withdrawal queue.
                                        </div>

                                        {actionFeedback && (
                                            <div className={`p-3 rounded-xl text-xs font-bold ${actionFeedback.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
                                                {actionFeedback.message}
                                            </div>
                                        )}

                                        <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 pt-2">
                                            <button
                                                onClick={() => setActionModalOpen(false)}
                                                disabled={actionProcessing}
                                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 rounded-xl transition-colors text-xs"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleExecuteDelete}
                                                disabled={actionProcessing}
                                                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-2.5 rounded-xl transition-colors text-xs flex items-center justify-center gap-1.5 shadow-lg shadow-red-900/30"
                                            >
                                                {actionProcessing ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                                {actionProcessing ? "Deleting..." : "Confirm Delete"}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 border border-red-500/30">
                                                <X size={20} />
                                            </div>
                                            <div>
                                                <h3 className="text-base font-bold text-white">Decline Withdrawal</h3>
                                                <p className="text-xs text-slate-400">Reject request and record reason</p>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-slate-400 text-xs mb-1.5">Reason for Declining</label>
                                            <textarea
                                                rows={3}
                                                placeholder="e.g. Account name mismatch with KYC, invalid bank details..."
                                                value={declineReason}
                                                onChange={(e) => setDeclineReason(e.target.value)}
                                                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-xs focus:outline-none focus:border-red-500"
                                            />
                                        </div>

                                        {actionFeedback && (
                                            <div className={`p-3 rounded-xl text-xs font-bold ${actionFeedback.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
                                                {actionFeedback.message}
                                            </div>
                                        )}

                                        <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 pt-2">
                                            <button
                                                onClick={() => setActionModalOpen(false)}
                                                disabled={actionProcessing}
                                                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2.5 rounded-xl transition-colors text-xs"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleExecuteDecline}
                                                disabled={actionProcessing}
                                                className="flex-1 bg-red-500 hover:bg-red-400 text-white font-bold py-2.5 rounded-xl transition-colors text-xs flex items-center justify-center gap-1.5"
                                            >
                                                {actionProcessing ? <RefreshCw size={14} className="animate-spin" /> : <X size={14} />}
                                                {actionProcessing ? "Declining..." : "Confirm Decline"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                );
            })()}
        </div>
    );
};

export default FinancePage;